<template>
    <div id="container">
        <slot name="before"/>
        <div ref="map" id='map'>
            <slot/>
            <div v-if="!hideIndicators" class="color-indicator">
                <template v-if="!onlyMarkers">
                <span class="color-identifier"
                      :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.PENDING+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">Pending</span>
                    <span class="color-identifier"
                          :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.CONFIRMED+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">Accepted</span>
                    <span class="color-identifier"
                          :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.STARTED+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">Started</span>
                    <span class="color-identifier"
                          :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.ARRIVED+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">Arrived</span>
                    <span class="color-identifier"
                          :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.PICKED_UP+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">Picked up</span>
                    <span class="color-identifier"
                          :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.DONE+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">Done</span>
                </template>
                <template v-else>
                    <span class="color-identifier"
                          :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.DONE+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">From</span>
                    <span class="color-identifier"
                          :style="'background-color: '+api.CONSTANTS.COLOR_INDICATORS.PICKED_UP+';min-width: 10px;padding: 3px 7px;font-size: 12px;font-weight: 700;line-height: 1;color: #fff;text-align: center;white-space: nowrap;vertical-align: middle;border-radius: 10px;'">To</span>
                </template>
            </div>
            <div v-if="!onlyMarkers" class='filter-ctrl'>
                <input id='filter-input' type='text' name='filter' placeholder='Filter by name' v-model="search"/>
            </div>
        </div>
        <slot name="after"/>
    </div>
</template>

<script>

    import Mapbox from "mapbox-gl";
    import PENDING_MARKER from "@/assets/markers/pending.png";
    import ACCEPTED_MARKER from "@/assets/markers/accepted.png";
    import STARTED_MARKER from "@/assets/markers/started.png";
    import ARRIVED_MARKER from "@/assets/markers/arrived.png";
    import PICKED_UP_MARKER from "@/assets/markers/picked_up.png";
    import DONE_MARKER from "@/assets/markers/done.png";
    import moment from "moment";
    import socketEventsMixin from "@/mixins/socketEventsMixin";
    import RidePage from "@/views/RidePage";
    import hasIntervalsMixin from "@/mixins/hasIntervalsMixin";

    const colors = ["#F00", "#00F"];

    let colorIdx = 0;
    export default {
        components: {RidePage},
        name: "Map",
        title: 'Map',
        mixins: [socketEventsMixin, hasIntervalsMixin],
        events() {
            return {
                [this.api.SOCKET.EVENTS.CAPTAIN_UPDATED_LOCATION]: this.captainUpdateLocation,
                [this.api.SOCKET.EVENTS.NEW_ACTION]: this.updateStatus,
            }
        },
        intervals() {
            return [
                [this.updateDestinationsColors, 200]
            ];
        },
        props: {
            height: {
                type: String,
                default: "calc(100vh - 64px - 50px)"
            },
            appendMarkers: {
                type: Array,
                default: () => []
            },
            center: {
                type: Array,
                default() {
                    return this.api.mapBox.center
                }
            },
            onlyMarkers: {
                type: Boolean,
                default: false
            },
            hideIndicators: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                map: null,
                data: {},
                popup: null,
                activePopup: null,
                search: null
            }
        },
        watch: {
            search(value) {
                for (let captain_id in this.data)
                    if (this.map.getLayer('captain-' + captain_id))
                        this.map.setLayoutProperty('captain-' + captain_id, 'visibility',
                            this.data[captain_id].captain_name.toLowerCase().indexOf(value.toLowerCase()) > -1 ? 'visible' : 'none');
            }
        },
        mounted() {
            Mapbox.accessToken = this.api.mapBox.accessToken;
            this.map = new Mapbox.Map(Object.assign({
                    container: 'map'
                },
                this.api.mapBox,
                {center: this.center}
            ));
            this.map.on('load', () => {
                this.map.resize();
                if (!this.onlyMarkers) {
                    this.map.loadImage(PENDING_MARKER, (_, data) => this.map.addImage('PENDING', data));
                    this.map.loadImage(ACCEPTED_MARKER, (_, data) => this.map.addImage('ACCEPTED', data));
                    this.map.loadImage(STARTED_MARKER, (_, data) => this.map.addImage('STARTED', data));
                    this.map.loadImage(ARRIVED_MARKER, (_, data) => this.map.addImage('ARRIVED', data));
                    this.map.loadImage(PICKED_UP_MARKER, (_, data) => this.map.addImage('PICKED_UP', data));
                    this.map.loadImage(DONE_MARKER, (_, data) => this.map.addImage('DONE', data));
                    this.axios.get('captain/map')
                        .then(({data}) => {
                            for (let item of data.data) {
                                this.captainUpdateLocation(item);
                                this.updateDestinations(item.captain_id, item.destinations);
                            }
                        })
                        .catch(err => console.log(err.message));
                }
                for (let marker of this.appendMarkers)
                    marker.addTo(this.map);

            });
            this.popup = new Mapbox.Popup({closeButton: false, closeOnClick: false, className: "map-popup"});
            this.map.addControl(new Mapbox.FullscreenControl());
            this.$refs.map.style.height = this.height;
        },
        methods: {
            updateDestinationsColors() {
                if (!this.map)
                    return;
                for (let captain_id in this.data) {
                    if (this.activePopup != 'captain-' + captain_id)
                        continue;
                    const destinationLayerId = 'captain-destinations-' + captain_id;
                    if (this.map.getLayer(destinationLayerId))
                        this.map.setLayoutProperty(destinationLayerId, 'icon-image',
                            colorIdx % 2 ? "PICKED_UP" : "DONE");

                }
                colorIdx++;
            },
            highlightCaptain(captainId) {
                for (let captain_id in this.data) {
                    if (this.map.getLayer('captain-' + captain_id))
                        this.map.setLayoutProperty('captain-' + captain_id, 'visibility',
                            captain_id == captainId || captainId === null ? 'visible' : 'none');
                    const destinationLayerId = 'captain-destinations-' + captain_id;
                    if (this.map.getLayer(destinationLayerId))
                        this.map.setLayoutProperty(destinationLayerId, 'visibility', captain_id == captainId ? 'visible' : 'none');
                }
            },
            updateDestinations(captain_id, destinations) {
                this.data[captain_id] = Object.assign({}, this.data[captain_id] || {}, {
                    destinations
                });

                const captainLayerId = 'captain-' + captain_id;
                const destinationLayerId = 'captain-destinations-' + captain_id;

                let geojson = {
                    type: "FeatureCollection",
                    features: destinations.map(
                        ({location, name, details}) => ({
                            type: "Feature",
                            geometry: location
                        })
                    )
                };

                if (!this.map.getLayer(destinationLayerId)) {
                    this.map.addLayer({
                        id: destinationLayerId,
                        type: "symbol",
                        source: {
                            type: "geojson",
                            data: geojson
                        },

                        layout: {
                            "icon-image": "PENDING",
                            "icon-size": 0.5,
                            "visibility": captainLayerId === this.activePopup ? "visible" : "none",
                        }
                    });
                } else {
                    this.map.getSource(destinationLayerId).setData(geojson);
                }
            },
            captainUpdateLocation({captain_id, captain_name, location: geometry, last_seen, last_action, arrival_date}) {
                const LayerID = 'captain-' + captain_id;
                if (!last_action) {
                    last_action = this.data[captain_id] ? this.data[captain_id].last_action : "PENDING";
                }
                this.data[captain_id] = Object.assign(this.data[captain_id] ? this.data[captain_id] : {}, {
                    captain_name,
                    location: geometry,
                    last_seen,
                    last_action,
                    arrival_date
                });
                if (this.activePopup === LayerID) {
                    this.popup.setHTML(`
                           <div class="font-weight-bold">
                              ${this.data[captain_id].captain_name}
                              <br>
                              Last Seen : ${moment(this.data[captain_id].last_seen).format('HH:mm:ss - MMM DD YYYY')}
                              <br>
                              Last Action : ${this.data[captain_id].last_action}
                              <br>
                              Arrival Date : ${this.data[captain_id].arrival_date}
                              ${
                        this.data[captain_id].destinations && this.data[captain_id].destinations.length ?
                            `
                                            <br/>
                                            To: ${this.data[captain_id].destinations.map(i => i.name).join(",")}
                                            <br/>
                                            Details: ${this.data[captain_id].destinations.map(i => i.details).join(",")}
                                        `
                            :
                            '"'
                    }
                            </div>
                            `);
                }
                if (!this.map.getLayer(LayerID)) {
                    this.map.addLayer({
                        id: LayerID,
                        type: "symbol",
                        source: {
                            type: "geojson",
                            data: {
                                type: "Feature",
                                geometry
                            }
                        },
                        layout: {
                            "icon-image": !!last_action ? last_action : "PENDING",
                            "icon-size": 0.5,
                            "icon-allow-overlap": true
                        }
                    });
                    this.map.on('mouseenter', LayerID, (e) => {
                        if (!!this.activePopup)
                            return;
                        this.map.getCanvas().style.cursor = 'pointer';
                        const {coordinates} = this.data[captain_id].location;
                        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180)
                            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                        this.activePopup = LayerID;
                        this.highlightCaptain(captain_id);
                        this.popup.setLngLat(coordinates)
                            .setHTML(`
                           <div class="font-weight-bold">
                              ${this.data[captain_id].captain_name}
                              <br>
                              Last Seen : ${moment(this.data[captain_id].last_seen).format('HH:mm:ss - MMM DD YYYY')}
                              <br>
                              Last Action : ${this.data[captain_id].last_action}
                              <br>
                              Arrival Date : ${this.data[captain_id].arrival_date}
                            ${
                                this.data[captain_id].destinations && this.data[captain_id].destinations.length ?
                                    `
                                            <br/>
                                            To: ${this.data[captain_id].destinations.map(i => i.name).join(",")}
                                            <br/>
                                            Details: ${this.data[captain_id].destinations.map(i => i.details).join(",")}
                                        `
                                    :
                                    ''
                            }
                            </div>
                            `)
                            .addTo(this.map);
                    });
                    this.map.on('mouseleave', LayerID, () => {
                        if (this.activePopup != LayerID)
                            return;
                        this.highlightCaptain(null);
                        if (this.activePopup === LayerID) {
                            this.map.getCanvas().style.cursor = '';
                            this.popup.remove();
                            this.activePopup = null;
                        }
                    });
                } else {
                    this.map.getSource(LayerID).setData({type: 'Feature', geometry});
                }
            },
            updateStatus({captain_id, status, destinations}) {
                if (this.map.getLayer('captain-' + captain_id)) {
                    this.map.setLayoutProperty('captain-' + captain_id, 'icon-image', status.toUpperCase());
                    this.data[captain_id].last_action = status;
                }
                if (!!destinations)
                    this.updateDestinations(captain_id, destinations);
            }
        }
    }
</script>

<style scoped>
    #map {
        width: 100%;
    }

    #container {
        overflow: hidden !important;
    }

    .filter-ctrl {
        position: absolute;
        top: 10px;
        right: 50px;
        z-index: 1;
        width: 180px;
    }

    .filter-ctrl input[type=text] {
        font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif;
        width: 100%;
        border: 0;
        background-color: #fff;
        height: 40px;
        margin: 0;
        color: rgba(0, 0, 0, .5);
        padding: 10px;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
        border-radius: 3px;
    }

    .color-indicator {
        position: absolute;
        top: 0;
        left: 0;
        margin: 16px;
        z-index: 1;
    }

    * >>> .mapboxgl-popup-content {
        background-color: #FFFFFFAA;
    }

</style>